import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';
import { convertGoogleDriveUrl, isVideoUrl } from '@/lib/urlUtils';
import Lightbox from '@/components/Lightbox';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_843f7c3a-3692-4728-b14f-ba431557a9c8/artifacts/qxen4yga_Ray%20Creators%20-%20Logo.png";

const HomePage = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projects, testimonialData, logos] = await Promise.all([
        publicApi.getProjects({ featured: true }),
        publicApi.getTestimonials(),
        publicApi.getClientLogos(),
      ]);
      setFeaturedProjects(projects.slice(0, 6));
      setTestimonials(testimonialData);
      setClientLogos(logos);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-gradient py-10 md:py-16" data-testid="hero-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.img
              src={LOGO_URL}
              alt="Ray Creators"
              className="h-32 w-32 md:h-44 md:w-44 mx-auto mb-8"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            />
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="font-serif text-foreground uppercase tracking-wider">Ray</span>
              <span className="font-script text-primary text-6xl md:text-8xl ml-2">Creators</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Crafting visual stories that captivate and inspire. From branding to packaging, 
              we bring your creative vision to life with passion and precision.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="btn-primary rounded-full px-8" data-testid="hero-gallery-btn">
                <Link to="/gallery">
                  View Gallery
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8" data-testid="hero-contact-btn">
                <Link to="/contact">Get in Touch</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Creations */}
      <section className="py-20 md:py-32" data-testid="featured-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-4">
              <span className="font-serif uppercase tracking-wider">Featured</span>
              <span className="font-script text-primary text-5xl md:text-6xl ml-2">Creations</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              A showcase of our most impactful design work
            </p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-muted animate-pulse rounded-xl" />
              ))}
            </div>
          ) : featuredProjects.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 stagger-children">
              {featuredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className="featured-card-wrapper cursor-pointer group"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => setSelectedProject(project)}
                  data-testid={`featured-project-${index}`}
                >
                  <div className="featured-card-inner">
                  {isVideoUrl(project.thumbnail) ? (
                    <video
                      src={convertGoogleDriveUrl(project.thumbnail)}
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={convertGoogleDriveUrl(project.thumbnail)}
                      alt={project.title}
                    />
                  )}
                  <div className="overlay absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="text-center text-white p-4">
                      <h3 className="font-serif text-xl mb-2">{project.title}</h3>
                      <p className="text-sm opacity-80">Click to view</p>
                    </div>
                  </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No featured projects yet. Check back soon!</p>
            </div>
          )}

          <div className="text-center mt-10">
            <Button asChild variant="outline" size="lg" className="rounded-full px-8" data-testid="view-all-btn">
              <Link to="/gallery">
                View All Projects
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 md:py-32 bg-muted" data-testid="testimonials-section">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-semibold mb-4">
                <span className="font-serif uppercase tracking-wider">Client</span>
                <span className="font-script text-primary text-5xl md:text-6xl ml-2">Stories</span>
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Hear from our happy clients
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto relative">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="testimonial-card text-center"
                data-testid="testimonial-card"
              >
                <Quote className="h-12 w-12 text-primary/20 mx-auto mb-6" />
                <p className="text-lg md:text-xl text-foreground mb-6 leading-relaxed">
                  "{testimonials[currentTestimonial]?.content}"
                </p>
                <div className="flex items-center justify-center gap-4">
                  {testimonials[currentTestimonial]?.avatar && (
                    <img
                      src={testimonials[currentTestimonial].avatar}
                      alt={testimonials[currentTestimonial].client_name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  )}
                  <div className="text-left">
                    <p className="font-semibold text-foreground">
                      {testimonials[currentTestimonial]?.client_name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {testimonials[currentTestimonial]?.company}
                    </p>
                  </div>
                </div>
              </motion.div>

              {testimonials.length > 1 && (
                <div className="flex justify-center gap-4 mt-8">
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full"
                    onClick={prevTestimonial}
                    data-testid="testimonial-prev"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <div className="flex items-center gap-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentTestimonial ? 'bg-primary' : 'bg-border'
                        }`}
                        onClick={() => setCurrentTestimonial(index)}
                        data-testid={`testimonial-dot-${index}`}
                      />
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full"
                    onClick={nextTestimonial}
                    data-testid="testimonial-next"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Client Logos */}
      {clientLogos.length > 0 && (
        <section className="py-20 md:py-32" data-testid="clients-section">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-semibold mb-4">
                <span className="font-serif uppercase tracking-wider">Trusted</span>
                <span className="font-script text-primary text-5xl md:text-6xl ml-2">By</span>
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Brands we've had the pleasure to work with
              </p>
            </motion.div>

            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {clientLogos.map((logo, index) => (
                <motion.div
                  key={logo.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="client-logo"
                  data-testid={`client-logo-${index}`}
                >
                  <img
                    src={logo.image}
                    alt={logo.name}
                    className="h-12 md:h-16 w-auto object-contain"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-foreground text-background" data-testid="cta-section">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-4">
              <span className="font-serif uppercase tracking-wider text-white">Ready to</span>
              <span className="font-script text-primary text-5xl md:text-6xl ml-2">Create?</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
              Let's bring your vision to life. Get in touch and let's start crafting 
              your visual story together.
            </p>
            <Button asChild size="lg" className="btn-primary rounded-full px-8" data-testid="cta-btn">
              <Link to="/contact">
                Start Your Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedProject && (
        <Lightbox
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </div>
  );
};

export default HomePage;
